import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Loader2, Pencil, X } from "lucide-react";

// Generic per-student CRUD table with an inline add form.
// fields: [{ name, label, type: text|number|select|date, options?, required?, default? }]
// columns: [{ name, label, render?(row) }]
export default function RecordManager({ studentId, readOnly, kind, title, description, fields, columns, renderExtra, renderActions }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = fields.reduce((a, f) => ({ ...a, [f.name]: f.default ?? "" }), {});
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${kind}/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [kind, studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && (form[f.name] === "" || form[f.name] == null)) {
        toast.error(`${f.label} zorunlu`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] !== "") payload[f.name] = Number(payload[f.name]);
      });
      if (editId) {
        await api.put(`/${kind}/${studentId}/${editId}`, payload);
        toast.success("Kayıt güncellendi");
        setEditId(null);
      } else {
        await api.post(`/${kind}/${studentId}`, payload);
        toast.success("Kayıt eklendi");
      }
      setForm(blank);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    const f = {};
    fields.forEach((fd) => {
      f[fd.name] = row[fd.name] ?? fd.default ?? "";
    });
    setForm(f);
    setEditId(row.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(blank);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/${kind}/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold">{title}</h3>
          {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </div>
        {!readOnly && renderActions && <div>{renderActions(load)}</div>}
      </div>

      {renderExtra && renderExtra(items)}

      {!readOnly && (
        <form onSubmit={submit} className="card-ta p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {fields.map((f) => (
              <div key={f.name} className={f.wide ? "col-span-2" : ""}>
                <label className="ta-label">{f.label}</label>
                {f.type === "select" ? (
                  <select
                    className="ta-input"
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`field-${f.name}`}
                  >
                    <option value="">Seçiniz</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="ta-input"
                    type={f.type}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`field-${f.name}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={saving} className={editId ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"} data-testid={`${kind}-add-btn`}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : editId ? <Pencil size={16} /> : <Plus size={16} />}
              <span className="ml-1">{editId ? "Güncelle" : "Ekle"}</span>
            </Button>
            {editId && (
              <Button type="button" variant="outline" onClick={cancelEdit} data-testid={`${kind}-cancel-edit`}>
                <X size={16} />
                <span className="ml-1">Vazgeç</span>
              </Button>
            )}
          </div>
        </form>
      )}

      <div className="card-ta overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                {columns.map((c) => (
                  <th key={c.name} className="px-4 py-3 font-medium whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                {!readOnly && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody data-testid={`${kind}-table`}>
              {loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500">
                    <Loader2 className="animate-spin inline" size={20} />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500">
                    Henüz kayıt yok.
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-200 hover:bg-slate-50">
                    {columns.map((c) => (
                      <td key={c.name} className="px-4 py-3 whitespace-nowrap text-slate-800">
                        {c.render ? c.render(row) : row[c.name] ?? "-"}
                      </td>
                    ))}
                    {!readOnly && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => startEdit(row)}
                          className="text-slate-500 hover:text-amber-600 mr-3"
                          data-testid={`edit-${row.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => remove(row.id)}
                          className="text-slate-500 hover:text-rose-600"
                          data-testid={`delete-${row.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function netBadge(n) {
  const v = Number(n || 0);
  const cls = v >= 0 ? "text-emerald-600" : "text-rose-600";
  return <span className={`font-mono font-semibold ${cls}`}>{v.toFixed(2)}</span>;
}
